import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff, Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Toaster, toast } from '../components/ui/sonner';
import MintedLogo from '../components/common/MintedLogo';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useApp();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const result = await login(email, password);
      if (result.success) {
        toast.success('Welcome back!');
        navigate('/');
      } else {
        toast.error(result.error || 'Login failed');
      }
    } catch (error) {
      toast.error('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) {
      toast.error('Please enter your email');
      return;
    }
    setForgotLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail })
      });
      if (res.ok) {
        setForgotSent(true);
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Something went wrong');
      }
    } catch (err) {
      toast.error('Failed to send reset email. Please try again.');
    } finally {
      setForgotLoading(false);
    }
  };

  // Forgot Password View
  if (showForgot) {
    return (
      <div className="min-h-screen bg-minted-900 flex flex-col">
        <Toaster position="top-center" richColors />
        <div className="flex-1 flex flex-col items-center justify-center px-6 py-12">
          <MintedLogo size="xl" showText={true} glowEffect={true} />
          <p className="text-minted-300 text-xs tracking-widest uppercase mt-3">Buy & Sell Luxury Near You</p>
        </div>

        <div className="bg-stone-50 rounded-t-2xl px-6 py-8 shadow-xl">
          {forgotSent ? (
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Check Your Email</h2>
              <p className="text-gray-500 text-sm mb-4">
                We've sent a password reset link to <strong>{forgotEmail}</strong>. Check your inbox and spam folder.
              </p>
              <Button
                onClick={() => { setForgotSent(false); }}
                variant="outline"
                className="w-full mb-3"
                data-testid="resend-reset-email-btn"
              >
                Didn't receive it? Send again
              </Button>
              <Button
                onClick={() => { setShowForgot(false); setForgotSent(false); setForgotEmail(''); }}
                className="w-full bg-minted-600 hover:bg-minted-700 h-12"
                data-testid="back-to-login-btn"
              >
                Back to Login
              </Button>
            </div>
          ) : (
            <>
              <button
                onClick={() => setShowForgot(false)}
                className="flex items-center gap-1 text-gray-500 text-sm mb-4 hover:text-gray-700"
                data-testid="forgot-back-btn"
              >
                <ArrowLeft className="w-4 h-4" /> Back to Login
              </button>
              <div className="text-center mb-6">
                <Mail className="w-10 h-10 text-minted-600 mx-auto mb-2" />
                <h2 className="text-xl font-bold text-gray-900">Forgot Password?</h2>
                <p className="text-sm text-gray-500 mt-1">Enter your email and we'll send you a reset link</p>
              </div>
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div>
                  <Label htmlFor="forgotEmail">Email Address</Label>
                  <Input
                    id="forgotEmail"
                    type="email"
                    placeholder="your@email.com"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    required
                    className="mt-1"
                    data-testid="forgot-email-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-minted-600 hover:bg-minted-700 h-12 text-base"
                  disabled={forgotLoading}
                  data-testid="send-reset-link-btn"
                >
                  {forgotLoading ? 'Sending...' : 'Send Reset Link'}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-minted-900 flex flex-col">
      <Toaster position="top-center" richColors />
      {/* Header */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12">
        <MintedLogo size="xl" showText={true} glowEffect={true} />
        <p className="text-minted-300 text-xs tracking-widest uppercase mt-3">Buy & Sell Luxury Near You</p>
      </div>

      {/* Login Form */}
      <div className="bg-stone-50 rounded-t-2xl px-6 py-8 shadow-xl">
        <h2 className="font-display text-xl text-stone-900 mb-6 text-center">Sign In</h2>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="email" className="text-[11px] tracking-wider uppercase text-stone-500">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="your@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="mt-1 rounded-sm border-stone-200"
              data-testid="login-email-input"
            />
          </div>
          
          <div>
            <Label htmlFor="password" className="text-[11px] tracking-wider uppercase text-stone-500">Password</Label>
            <div className="relative mt-1">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="rounded-sm border-stone-200"
                data-testid="login-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between text-sm">
            <label className="flex items-center gap-2">
              <input type="checkbox" className="rounded-sm border-stone-300 text-minted-700 focus:ring-minted-600" />
              <span className="text-stone-500 text-xs">Remember me</span>
            </label>
            <button
              type="button"
              onClick={() => { setShowForgot(true); setForgotEmail(email); }}
              className="text-minted-700 hover:underline text-xs"
              data-testid="forgot-password-btn"
            >
              Forgot password?
            </button>
          </div>

          <Button
            type="submit"
            className="w-full bg-minted-900 hover:bg-minted-700 h-12 text-sm tracking-wider uppercase rounded-sm"
            disabled={loading}
            data-testid="login-submit-btn"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </Button>
        </form>

        {/* Register Link */}
        <p className="text-center text-stone-500 text-xs mt-6">
          Don't have an account?{' '}
          <Link to="/register" className="text-minted-700 font-medium hover:underline">
            Sign Up
          </Link>
        </p>
      </div>
    </div>
  );
};

export default LoginPage;